import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const warehouseKeys = {
  all: ['warehouses'],
  lists: () => [...warehouseKeys.all, 'list'],
  details: () => [...warehouseKeys.all, 'detail'],
  detail: (id) => [...warehouseKeys.details(), id],
  byCode: (code) => [...warehouseKeys.all, 'byCode', code],
};

export const useWarehouses = (options = {}) => {
  return useQuery({
    queryKey: warehouseKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('warehouses');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    ...options,
  });
};

export const useWarehouse = (id, options = {}) => {
  return useQuery({
    queryKey: warehouseKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`warehouses/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useWarehouseByCode = (code, options = {}) => {
  return useQuery({
    queryKey: warehouseKeys.byCode(code),
    queryFn: async () => {
      const response = await apiClient.get(`warehouses/by-code/${code}`);
      return response.data;
    },
    enabled: !!code,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateWarehouse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (warehouseData) => {
      const response = await apiClient.post('warehouses', warehouseData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating warehouse:', error);
    },
  });
};

export const useUpdateWarehouse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`warehouses/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(warehouseKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: warehouseKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating warehouse:', error);
    },
  });
};

export const useDeleteWarehouse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`warehouses/${id}`);
      return response.data;
    },
    onSuccess: (_data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: warehouseKeys.lists() });
      queryClient.removeQueries({ queryKey: warehouseKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting warehouse:', error);
    },
  });
};
